import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Warning, ArrowCounterClockwise, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const FailedRefundsPanel = () => {
  const { language, API } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/failed-refunds');
      setRows(data || []);
    } catch { /* silent */ }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const act = async (r, action) => {
    if (action === 'retry' && !window.confirm(L(`Refund ${r.amount.toFixed(2)} CHF to ${r.user_name || 'customer'} now?`, `${r.amount.toFixed(2)} CHF jetzt an ${r.user_name || 'Kunde'} zurückerstatten?`))) return;
    if (action === 'dismiss' && !window.confirm(L('Mark as handled manually (e.g. refunded in Datatrans backoffice)?', 'Als manuell erledigt markieren (z.B. im Datatrans-Backoffice erstattet)?'))) return;
    setBusy(r.id);
    try {
      const { data } = await API.post(`/admin/failed-refunds/${r.id}/${action}`);
      toast.success(data.message);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Refund failed', 'Rückerstattung fehlgeschlagen'));
      load();
    } finally {
      setBusy(null);
    }
  };

  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 sm:p-5 space-y-3" data-testid="failed-refunds-panel">
      <div className="flex items-center gap-2 text-red-800 font-bold">
        <Warning size={20} weight="fill" />
        {L('Open refunds', 'Offene Rückerstattungen')} ({rows.length})
      </div>
      <p className="text-xs text-red-700">
        {L('Automatic refund failed for these payments. The money is still with the club.', 'Bei diesen Zahlungen ist die automatische Rückerstattung fehlgeschlagen. Das Geld liegt noch beim Club.')}
      </p>
      {rows.map((r) => (
        <div key={r.id} className="bg-white rounded-xl border border-red-100 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`failed-refund-${r.id}`}>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold text-slate-900">
              {r.user_name || r.user_email || L('Unknown customer', 'Unbekannter Kunde')} · <span data-testid={`failed-refund-amount-${r.id}`}>{r.amount.toFixed(2)} CHF</span>
            </div>
            <div className="text-xs text-slate-500 truncate">{r.description || r.purpose} · TX {r.transaction_id}</div>
            <div className="text-xs text-red-600 break-words">{r.error} ({r.attempts}x)</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" disabled={busy === r.id} onClick={() => act(r, 'retry')} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`failed-refund-retry-${r.id}`}>
              <ArrowCounterClockwise size={14} className="mr-1" />{L('Refund now', 'Jetzt erstatten')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => act(r, 'dismiss')} data-testid={`failed-refund-dismiss-${r.id}`}>
              <Check size={14} className="mr-1" />{L('Handled', 'Erledigt')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
